import React, { useEffect, useState } from "react";
import { ClipLoader } from "react-spinners";
import { FaCertificate, FaExternalLinkAlt, FaCalendarAlt, FaBuilding } from "react-icons/fa";
import certificatesData from "../../data/certificates";

const Certificates = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failedImages, setFailedImages] = useState({});

  useEffect(() => {
    const sorted = [...certificatesData].sort((a, b) => a.priority - b.priority);
    setCertificates(sorted);
    setLoading(false);
  }, []);

  const handleImageError = (certId) => {
    setFailedImages(prev => ({ ...prev, [certId]: true }));
  };

  return (
    <section
      id="certificates"
      className="py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50
        dark:from-[#0a0a18] dark:to-[#0f0f1a] transition-colors duration-300"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-14">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#f29f67] to-[#3b8ff3] 
              flex items-center justify-center shadow-lg">
              <FaCertificate className="text-white text-3xl" />
            </div>
          </div>
          <h2
            className="text-3xl font-extrabold uppercase tracking-widest
              text-gray-800 dark:text-white mb-3"
            style={{ fontFamily: "'Syne', sans-serif" }}
          >
            Certifications
          </h2>
          <div className="w-16 h-1 mx-auto rounded-full bg-gradient-to-r from-[#f29f67] to-[#3b8ff3] mb-4" />
          <p className="text-gray-500 dark:text-gray-400 text-sm max-w-xl mx-auto">
            Professional certifications and courses I've completed to enhance my skills
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <ClipLoader color="#f29f67" size={48} />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {certificates.map((cert) => (
              <div
                key={cert._id}
                className="group relative rounded-2xl overflow-hidden
                  bg-white/90 dark:bg-white/5
                  backdrop-blur-sm
                  border border-gray-200 dark:border-white/10
                  hover:border-[#f29f67]/40 dark:hover:border-[#f29f67]/40
                  hover:shadow-2xl hover:shadow-[#f29f67]/10
                  hover:-translate-y-2
                  transition-all duration-300
                  flex flex-col"
              >
                {/* Organization Badge */}
                <div className="absolute top-3 left-3 z-10
                  px-3 py-1.5 rounded-lg text-xs font-bold
                  bg-gradient-to-r from-[#f29f67] to-[#f29f67]/80
                  text-white shadow-md
                  flex items-center gap-1.5">
                  <FaBuilding size={10} />
                  {cert.organization}
                </div>

                {/* Date Badge (if available) */}
                {cert.date && (
                  <div className="absolute top-3 right-3 z-10
                    px-2 py-1 rounded-lg text-[10px] font-semibold
                    bg-black/50 backdrop-blur-sm
                    text-white
                    flex items-center gap-1">
                    <FaCalendarAlt size={8} />
                    {cert.date}
                  </div>
                )}

                {/* Certificate Image Section with Fallback */}
                <div className="relative overflow-hidden h-52 bg-gradient-to-br from-gray-100 to-gray-200 
                  dark:from-gray-800 dark:to-gray-900">
                  {!failedImages[cert._id] ? (
                    <img
                      src={cert.certificateImage || cert.certificateUrl}
                      alt={cert.title}
                      className="w-full h-full object-cover
                        group-hover:scale-110 transition-transform duration-700"
                      onError={() => handleImageError(cert._id)}
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center 
                      text-gray-400 dark:text-gray-500">
                      <FaCertificate size={64} className="mb-3 opacity-30" />
                      <span className="text-sm font-medium">Certificate Preview</span>
                      <span className="text-xs opacity-60">Image unavailable</span>
                    </div>
                  )}
                  
                  {/* Overlay Gradient on Hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent 
                    opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  {/* Hover Overlay Text */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 
                    group-hover:opacity-100 transition-all duration-300">
                    <span className="px-4 py-2 bg-white/90 dark:bg-black/80 rounded-lg text-xs font-semibold
                      text-gray-800 dark:text-white transform translate-y-4 group-hover:translate-y-0 transition-transform">
                      Click to view certificate
                    </span>
                  </div>
                </div>

                {/* Certificate Info */}
                <div className="p-5 flex flex-col flex-grow">
                  <h3
                    className="text-sm font-bold text-gray-800 dark:text-white text-center mb-3 
                      leading-snug line-clamp-2 min-h-[2.5rem]"
                    style={{ fontFamily: "'Syne', sans-serif" }}
                  >
                    {cert.title}
                  </h3>
                  
                  {/* Skills/Tags (if available) */}
                  {cert.skills && cert.skills.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 justify-center mb-4">
                      {cert.skills.slice(0, 3).map((skill, idx) => (
                        <span key={idx} 
                          className="px-2 py-0.5 bg-gray-100 dark:bg-white/10 
                            rounded-full text-[10px] font-medium 
                            text-gray-600 dark:text-gray-400">
                          {skill}
                        </span>
                      ))}
                      {cert.skills.length > 3 && (
                        <span className="px-2 py-0.5 bg-gray-100 dark:bg-white/10 
                          rounded-full text-[10px] font-medium 
                          text-gray-600 dark:text-gray-400">
                          +{cert.skills.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  {/* View Button */}
                  {(cert.certificateUrl || cert.certificateImage) && (
                    <a
                      href={cert.certificateUrl || cert.certificateImage}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group/btn flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-xs font-semibold
                        bg-gradient-to-r from-[#3b8ff3] to-[#2563eb] 
                        text-white
                        hover:shadow-lg hover:shadow-[#3b8ff3]/30 
                        hover:-translate-y-0.5
                        transition-all duration-200
                        relative overflow-hidden"
                    >
                      <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-white/0 to-white/20 
                        transform -translate-x-full group-hover/btn:translate-x-0 transition-transform duration-500" />
                      <FaExternalLinkAlt size={12} className="relative z-10" />
                      <span className="relative z-10">View Certificate</span>
                    </a>
                  )}
                </div>

                {/* Decorative Corner */}
                <div className="absolute bottom-0 left-0 w-16 h-16 
                  bg-gradient-to-tr from-[#f29f67]/5 to-transparent 
                  rounded-tr-2xl pointer-events-none" />
              </div>
            ))}
          </div>
        )}

        {/* Stats Section (Optional) */}
        {!loading && certificates.length > 0 && (
          <div className="mt-16 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              🎓 {certificates.length} professional certification{certificates.length !== 1 ? 's' : ''} earned
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Certificates;